<template>
  <ExpandedSideSection
    :loading="applicationsStore.isLoadingSelected || isInitialLoading"
    :no-data="!applicationsStore.selectedApplication && !applicationsStore.isLoadingSelected && initialLoadAttempted"
    :back-route="backRoute"
    empty-text="No application found"
  >
    <template #header-actions> </template>

    <!-- Application content -->
    <div class="p-4 bg-white border-[1px] rounded-lg border-gray-200">
      <JobApplicationUserDetails
        v-if="applicationsStore.selectedApplication?.user_details"
        :user-details="applicationsStore.selectedApplication?.user_details"
        :resume-file-details="applicationsStore.selectedApplication?.resume_file_details"
        :applicant-profile="applicationsStore.selectedApplication?.applicant_profile"
        :no-shadow="true"
      />

      <!-- Job Main Data Component -->
      <div class="px-4 py-2 mt-2 bg-gray-50 rounded-lg">
        <JobMainData
          :minimal="true"
          :role-data="roleData"
          :show-candidate-info="isGuide"
          :candidate-data="applicationsStore.selectedApplication?.user_details"
          :show-date="false"
        />
      </div>

      <!-- Application data -->
      <div class="mt-4 px-2 rounded-lg flex justify-between items-center">
        <ReferralRequestSentBadge v-if="(applicationsStore.selectedApplication as any)?.referral_request_email_sent" />

        <div class="p-1 cursor-default flex items-center text-gray-500 text-xs ml-auto">
          <Calendar class="w-4 h-4 mr-1" />
          Updated
          {{ applicationsStore.selectedApplication?.updated_at ? formatDate(applicationsStore.selectedApplication.updated_at) : '' }}
        </div>
      </div>

      <!-- Application Status Dropdowns -->
      <div v-if="applicationsStore.selectedApplication && selectedApplicationID" class="p-4 bg-white rounded-lg">
        <UserApplicationStatuses
          :selected-application="applicationsStore.selectedApplication"
          :application-id="selectedApplicationID"
          :disabled="!isGuide && !isOwner && !isCurrentUserReferrer"
          :is-current-user-referrer="isCurrentUserReferrer"
          :hide-referral-fields="isLimitedUser"
          @status-updated="() => {}"
          @refresh-requested="emit('refreshRequested')"
        />
        <p v-if="lastLegacyStatus" class="text-xs text-gray-500 mt-2">Last legacy status: {{ lastLegacyStatus }}</p>
      </div>

      <!-- Resume selector (referral/referrer flow only) -->
      <div
        v-if="
          applicationsStore.selectedApplication &&
          selectedApplicationID &&
          (isOwner || isGuide) &&
          !isLimitedUser &&
          applicationsStore.selectedApplication.referrers_exist_for_employer !== false
        "
        class="bg-gray-50 rounded-lg p-4 mt-2"
      >
        <ApplicationResumeSelector
          :application="applicationsStore.selectedApplication"
          :application-id="selectedApplicationID"
        />
      </div>

      <!-- Job Match Results -->
      <div v-if="applicationsStore.selectedApplication?.match_status && (isOwner || isGuide)" class="mt-2">
        <JobMatchResults 
          :application="applicationsStore.selectedApplication"
          @update="handleApplicationUpdate"
        />
      </div>

      <!-- Referral management (admin-only) -->
      <div v-if="isGuide && isAdmin" class="bg-gray-50 rounded-lg p-4 mt-2">
        <h2 class="text-md flex items-center mb-2">
          <Mail class="w-4 h-4 mr-2" />
          Referral management
        </h2>
        <ReferrerSelection
          v-if="isGuide && isAdmin && applicationsStore.selectedApplication && selectedApplicationID"
          :application="applicationsStore.selectedApplication"
          :application-id="selectedApplicationID"
          @referrer-updated="handleReferrerUpdated"
        />
        
        <div class="flex items-center gap-2 justify-end text-sm">
          <router-link
            class="mt-4 bg-lime-300 border-[1px] border-lime-300 rounded-md text-lime-900 font-light px-2 py-1 hover:bg-lime-400 hover:border-lime-400 hover:text-lime-800 transition-all hover:shadow-md hover:translate-y-[-1px] duration-200 flex items-center"
            :to="referralEmailRoute"
          >
            <Eye class="w-4 h-4 mr-2" />
            Preview referral email
          </router-link>
          <!-- <button
            class="mt-4 bg-lime-300 border-[1px] border-lime-300 rounded-md text-lime-900 font-light px-2 py-1 hover:bg-lime-400 hover:border-lime-400 hover:text-lime-800 transition-all hover:shadow-md hover:translate-y-[-1px] duration-200 flex items-center"
          >
            <Send class="w-3.5 h-3.5 mr-2" />
            Send referral email
          </button> -->
        </div>
      </div>

      <hr v-if="selectedApplicationID && (isOwner || isGuide)" class="w-full my-2" />

      <UserApplicationComments
        v-if="selectedApplicationID && (isOwner || isGuide)"
        :comments="applicationsStore.selectedApplication?.comments || undefined"
        :application-id="selectedApplicationID"
        :disabled="!isGuide && applicationsStore.selectedApplication?.user_details?.id !== userStore?.currentUser?.id"
        @comments-updated="handleCommentsUpdated"
      />

      <div
        v-if="(isOwner || isGuide) && selectedApplicationID"
        class="flex items-start w-full justify-between gap-2 h-fit"
      >
        <a
          v-if="isOwner && !isLimitedUser"
          :href="`http://interviewwatch.outtalent.com/my-interviews/create?job_application_id=${selectedApplicationID}`"
          target="_blank"
          rel="noopener noreferrer"
          class="border-blue-500 border-[1px] text-white bg-cosmicPurple hover:bg-cosmicPurple/80 hover:shadow-md hover:translate-y-[-1px] text-xs h-fit py-2 px-4 rounded-md transition-all flex items-center gap-1.5"
        >
          <MessagesSquare class="w-3.5 h-3.5" />
          Add Interview Entry
        </a>
        <Button
          v-if="isAdmin"
          @click="adminEditing = true"
          variant="outline"
          class="border-amber-500 border-[1px] text-amber-700 hover:bg-amber-50 text-xs h-fit py-2 flex items-center gap-1.5"
        >
          <ShieldCheck class="w-3.5 h-3.5" />
          Admin edit
        </Button>
        <Button
          @click="deleteApplication"
          variant="destructive"
          class="bg-transparent border-red-500 border-[1px] text-red-500 hover:text-white text-xs h-fit py-2"
        >
          Delete job application
        </Button>
      </div>
    </div>
    <UserApplicationDelete
      v-if="deleting && selectedApplicationID"
      :application-id="selectedApplicationID"
      @close="deleting = false"
      :all-applications="props.allApplications"
    />
    <AdminEditApplicationModal
      v-if="isAdmin && applicationsStore.selectedApplication && selectedApplicationID"
      :open="adminEditing"
      :application="applicationsStore.selectedApplication"
      :application-id="selectedApplicationID"
      @update:open="adminEditing = $event"
      @saved="handleAdminEditSaved"
    />
  </ExpandedSideSection>
</template>

<script setup lang="ts">
import { onMounted, ref, computed, watch, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import JobMainData from '@/components/common/JobMainData.vue'
import { Calendar, Eye, MessagesSquare, Mail, ShieldCheck } from 'lucide-vue-next'
import { useUserStore } from '@/stores/user'
import { useApplicationsStore } from '@/stores/applications'
import { formatDate } from '@/utils/formatDate'
import { useAuth } from '@/composables/useAuth'
import { useReferrersStore } from '@/stores/referrers'
import ReferrerSelection from '@/components/userApplications/ReferrerSelection.vue'
import ReferralRequestSentBadge from '@/components/common/badges/ReferralRequestSentBadge.vue'
import UserApplicationStatuses from '@/components/userApplications/UserApplicationStatuses.vue'
import JobMatchResults from '@/components/common/jobApplication/JobMatchResults.vue'
import ExpandedSideSection from '@/components/common/ExpandedSideSection.vue'
import JobApplicationUserDetails from '@/components/common/jobApplication/JobApplicationUserDetails.vue'
import UserApplicationComments from '@/components/userApplications/UserApplicationComments.vue'
import statusSteps from '@/const/statusSteps'
import UserApplicationDelete from '@/components/userApplications/UserApplicationDelete.vue'
import AdminEditApplicationModal from '@/components/userApplications/AdminEditApplicationModal.vue'
import ApplicationResumeSelector from '@/components/common/jobApplication/ApplicationResumeSelector.vue'
import { Button } from '@/components/ui/button'

interface Props {
  allApplications?: boolean
}
const props = withDefaults(defineProps<Props>(), { allApplications: false })

const emit = defineEmits<{
  'refreshRequested': []
}>()

const route = useRoute()
const applicationsStore = useApplicationsStore()
const referrersStore = useReferrersStore()

const selectedApplicationID = ref(route.params.application_id as string | undefined)

// Track loading states
const isInitialLoading = ref(true)
const initialLoadAttempted = ref(false)

const userStore = useUserStore()
const { isGuide, isReferrer, isAdmin, isLimitedUser } = useAuth()

const isOwner = computed(() => {
  return applicationsStore.selectedApplication?.user_details?.id === userStore?.currentUser?.id
})
const isCurrentUserReferrer = computed(() => {
  return isReferrer.value && route.path.startsWith('/my-referrals')
})

const lastLegacyStatus = computed(() => {
  const today = new Date()
  const novemberFirst2025 = new Date('2025-11-01')
  
  if (today > novemberFirst2025) {
    return false
  }
  return statusSteps.find((step) => step.code === applicationsStore.selectedApplication?.status)?.label
})

const deleting = ref(false)
const deleteApplication = () => {
  deleting.value = true
}

const adminEditing = ref(false)
const handleAdminEditSaved = (updatedApplication: any) => {
  applicationsStore.selectedApplication = updatedApplication
}

// Helper function to get query parameters for candidate navigation
const getCandidateQueryParams = () => {
  // Create a new object to hold the query parameters we want to pass back
  const queryParams: Record<string, string> = {}

  // Copy all existing query parameters except 'from' and 'candidateId'
  // which are specific to the application view
  Object.entries(route.query).forEach(([key, value]) => {
    if (key !== 'from' && key !== 'candidateId' && typeof value === 'string') {
      queryParams[key] = value
    }
  })

  return queryParams
}

const backRoute = computed(() => {
  if (!props.allApplications) {
    // Check if we have role_id param (jobs context) or not (user applications context)
    if (route.params.role_id) {
      // For jobs context, go back to the job view without the application_id to close the expanded view
      const queryWithoutApplicationId = { ...route.query }
      delete (queryWithoutApplicationId as any).application_id
      return { 
        name: 'Job details', 
        params: { role_id: route.params.role_id },
        query: queryWithoutApplicationId
      }
    } else {
      if (route.path.startsWith('/my-referrals')) {
        const queryWithoutApplicationId = { ...route.query }
        delete (queryWithoutApplicationId as any).application_id
        return {
          name: 'My Referrals',
          query: queryWithoutApplicationId
        }
      }
      // For user applications context, go back to User Applications
      const queryWithoutApplicationId = { ...route.query }
      delete (queryWithoutApplicationId as any).application_id
      return { 
        name: 'User Applications',
        query: queryWithoutApplicationId
      }
    }
  }
  if (route.query.from === 'candidates' && route.query.candidateId) {
    return {
      name: 'Candidate applications',
      params: { id: String(route.query.candidateId) },
      query: getCandidateQueryParams()
    }
  }
  // For All Applications view, go back to All Applications (closes the expanded view)
  return { name: 'All Applications', query: { ...route.query } }
})

// Watcher for route param changes (ensure it also updates selectedReferrerId)
watch(
  () => route.params.application_id,
  async (newId) => {
    const currentId = newId as string | undefined
    if (currentId && currentId !== selectedApplicationID.value) {
      selectedApplicationID.value = currentId
      isInitialLoading.value = true
      initialLoadAttempted.value = false
      try {
        await applicationsStore.fetchApplicationById(currentId)
        // selectedReferrerId will be updated by the other watcher via selectedApplication change
        isInitialLoading.value = false
        initialLoadAttempted.value = true
      } catch (error) {
        console.error('Failed to fetch application on route change:', error)
        isInitialLoading.value = false
        initialLoadAttempted.value = true
      }
    } else if (!currentId) {
      selectedApplicationID.value = undefined
      isInitialLoading.value = false
      initialLoadAttempted.value = true
    }
  },
  { immediate: true }
)

// Handle referrer update from child component
const handleReferrerUpdated = () => {
  // No need to update local ref since we're using store directly
}

// Polling for match status
const pollingInterval = ref<ReturnType<typeof setInterval> | null>(null)

const stopPolling = () => {
  if (pollingInterval.value) {
    clearInterval(pollingInterval.value)
    pollingInterval.value = null
  }
}

const startPolling = () => {
  stopPolling()
  pollingInterval.value = setInterval(async () => {
    if (selectedApplicationID.value) {
      // Background update, no loading state
      try {
        await applicationsStore.fetchApplicationById(selectedApplicationID.value)
      } catch (error) {
        console.error('Failed to poll application:', error)
      }
    }
  }, 20000)
}

watch(
  () => applicationsStore.selectedApplication?.match_status,
  (newStatus) => {
    if (newStatus === 'PENDING') {
      startPolling()
    } else {
      stopPolling()
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  stopPolling()
})


onMounted(async () => {
  try {
    const tasks = [
      userStore.fetchCurrentUser(),
      applicationsStore.fetchApplicationById(selectedApplicationID.value as string)
    ]
    // Referrers list is only needed for ReferrerSelection (guides only)
    if (isGuide.value) {
      tasks.push(referrersStore.fetchReferrers())
    }
    await Promise.all(tasks)
    isInitialLoading.value = false
    initialLoadAttempted.value = true
  } catch (error) {
    console.error('Failed to fetch application:', error)
    isInitialLoading.value = false
    initialLoadAttempted.value = true
  }
})


// Computed property to ensure stable role data for JobMainData component
const roleData = computed(() => {
  return applicationsStore.selectedApplication?.open_role_details || null
})

// Computed property for referral email route based on current context
const referralEmailRoute = computed(() => {
  // Prefer jobs context when role_id is present; otherwise, use All Applications preview
  if (route.params.role_id) {
    return {
      name: 'Job Application Referral email preview',
      params: {
        role_id: route.params.role_id,
        application_id: route.params.application_id
      },
      query: { ...route.query }
    }
  }

  return {
    name: 'All Applications Expanded Referral email preview',
    params: { application_id: route.params.application_id },
    query: { ...route.query }
  }
})

const handleCommentsUpdated = (comments: string) => {
  if (applicationsStore.selectedApplication) {
    applicationsStore.selectedApplication = {
      ...applicationsStore.selectedApplication,
      comments
    }
  }
}

const handleApplicationUpdate = (updatedApplication: any) => {
  applicationsStore.selectedApplication = updatedApplication
}
</script>
